import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react';
import BottomSheet from '../../../../components/ui/BottomSheet';
import { cn, genClientRequestId } from '../../../../lib/utils';
import { fmtValue } from '../../../broker/bookUtils';
import { SideBadge } from '../ManualBadges';
import { useManualExitAll } from '../../../../api/hooks';

const ROW_STATUS_LABELS = {
  EXITED: 'Exited',
  ALREADY_FLAT: 'Already Flat',
  FAILED: 'Failed',
  UNSUPPORTED: 'Unsupported',
};
const rowStatusCls = (s) =>
  s === 'EXITED' ? 'text-bull' : s === 'FAILED' ? 'text-bear' : 'text-txt-secondary';

// Self-contained Exit All flow: confirm → processing → result. Portaled to <body> so the viewport-fixed
// overlay escapes AppShell's transformed wrapper. Sends ONLY { client_request_id } via useManualExitAll.
// open: bool. strategy: card strategy. openPositions: active non-flat rows. deploymentId. onClose().
export default function ExitAllModal({ open, strategy, openPositions = [], deploymentId, onClose }) {
  const exitAll = useManualExitAll();
  const reqIdRef = useRef(null);          // stable client_request_id for one confirmed attempt
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Fresh attempt each time the modal opens: clear the prior id/result/error so a later, separately
  // opened operation generates a NEW uuid (and a stale result never leaks into a new open).
  useEffect(() => {
    if (open) {
      reqIdRef.current = null;
      setResult(null);
      setError(null);
    }
  }, [open]);

  if (!open || !strategy) return null;

  const submitting = exitAll.isPending;
  const count = openPositions.length;

  const runExitAll = () => {
    if (submitting) return; // hard guard against duplicate in-flight requests
    if (!reqIdRef.current) reqIdRef.current = genClientRequestId(); // one id per attempt; reused on retry
    setError(null);
    exitAll.mutate(
      { deploymentId, clientRequestId: reqIdRef.current },
      {
        onSuccess: (res) => { setResult(res); setError(null); },
        onError: (e) => { setError(e?.message || 'Exit All failed. Please try again.'); },
      },
    );
  };

  // Close is blocked while a request is in flight (prevents close/reopen from starting a second request).
  const handleClose = () => { if (!submitting) onClose?.(); };

  const phaseResult = !!result;

  const body = !phaseResult ? (
    <>
      <h3 className="font-display font-semibold text-txt-primary mb-1">Exit All Positions?</h3>
      <p className="text-xs font-mono text-txt-secondary mb-4">{strategy.strategyName}</p>

      <p className="text-xs font-mono text-txt-secondary mb-3">
        This will attempt to close all {count} currently open PAPER position{count === 1 ? '' : 's'} in this
        Manual strategy. Each position is processed independently — one failed row will not stop the rest.
      </p>

      <div className="rounded-md border border-bg-border bg-bg-elevated/40 divide-y divide-bg-border/50 mb-4 max-h-56 overflow-y-auto">
        {count === 0 ? (
          <p className="text-xs font-mono text-txt-muted px-3 py-3 text-center">No open positions to exit.</p>
        ) : (
          openPositions.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-3 py-2 text-xs font-mono">
              <span className="flex items-center gap-2">
                <span className="text-txt-primary">{p.symbol}</span>
                <SideBadge side={p.side} />
              </span>
              <span className="text-txt-muted">×{fmtValue(Math.abs(Number(p.netQty)) || p.qty)}</span>
            </div>
          ))
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-bear/40 bg-bear/10 px-3 py-2.5 mb-4">
          <XCircle size={14} className="text-bear shrink-0 mt-0.5" />
          <span className="text-2xs font-mono text-bear break-words">{error}</span>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-md border border-warn/30 bg-warn/10 px-3 py-2 mb-5">
        <AlertTriangle size={14} className="text-warn shrink-0 mt-0.5" />
        <span className="text-2xs font-mono text-txt-secondary">
          Exits only positions under this Manual PAPER strategy. No price is sent — each row squares off at market.
        </span>
      </div>

      {/* Mobile (<lg): sticky footer within the BottomSheet's scrollable body, matching
          the exit/cancel confirm sheets' idiom. lg: resets restore the desktop layout. */}
      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-2 bg-bg-card border-t border-bg-border flex justify-end gap-3 lg:static lg:mx-0 lg:px-0 lg:pt-0 lg:pb-0 lg:bg-transparent lg:border-0">
        <button type="button" onClick={handleClose} disabled={submitting} className="btn-ghost disabled:opacity-50 min-h-[48px] lg:min-h-0">
          Cancel
        </button>
        <button
          type="button"
          disabled={submitting || count === 0}
          onClick={runExitAll}
          className="btn-danger disabled:opacity-40 disabled:cursor-not-allowed min-h-[48px] lg:min-h-0"
        >
          {submitting ? 'Processing positions…' : error ? 'Retry Exit All' : 'Confirm Exit All'}
        </button>
      </div>
    </>
  ) : (
    <ResultView result={result} onClose={handleClose} />
  );

  return createPortal(
    <>
      {/* Desktop (>=lg): unchanged centered modal */}
      <div className="hidden lg:flex fixed inset-0 z-[1100] items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in shadow-panel">
          {body}
        </div>
      </div>

      {/* Mobile (<lg): bottom-sheet presentation of the same content. handleClose (not a
          raw onClose) preserves the existing in-flight guard on backdrop/X dismiss. */}
      <BottomSheet open onClose={handleClose} title="">
        {body}
      </BottomSheet>
    </>,
    document.body,
  );
}

function ResultView({ result, onClose }) {
  const status = result.status;
  const isClean = status === 'COMPLETED';
  const isNone = status === 'NO_OPEN_POSITIONS';
  const Icon = isClean ? CheckCircle : isNone ? Info : AlertTriangle;
  const iconCls = isClean ? 'text-bull' : isNone ? 'text-info' : 'text-warn';
  const heading = isClean ? 'Exit All completed' : isNone ? 'No open positions found' : 'Exit All completed with some errors';
  const rows = Array.isArray(result.results) ? result.results : [];

  return (
    <>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className={iconCls} />
        <h3 className="font-display font-semibold text-txt-primary">{heading}</h3>
      </div>

      {isNone ? (
        <p className="text-xs font-mono text-txt-secondary mt-2 mb-5">No open positions were found.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono text-txt-secondary mt-2 mb-3">
            <span><span className="text-bull">{result.exited_count}</span> exited</span>
            {result.already_flat_count > 0 && <span><span className="text-txt-muted">{result.already_flat_count}</span> already flat</span>}
            {result.failed_count > 0 && <span><span className="text-bear">{result.failed_count}</span> failed</span>}
            {result.unsupported_count > 0 && <span><span className="text-txt-muted">{result.unsupported_count}</span> unsupported</span>}
            <span className="text-txt-muted">({result.open_rows_found} open of {result.total_rows})</span>
          </div>

          <div className="rounded-md border border-bg-border overflow-hidden mb-5">
            <table className="w-full text-2xs font-mono">
              <thead>
                <tr className="border-b border-bg-border text-txt-label uppercase tracking-widest">
                  <th className="text-left py-1.5 px-2">Symbol</th>
                  <th className="text-left py-1.5 px-2">Result</th>
                  <th className="text-left py-1.5 px-2">Qty</th>
                  <th className="text-left py-1.5 px-2">Fill Price</th>
                  <th className="text-left py-1.5 px-2">Message</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.watchlist_row_id}-${i}`} className="border-b border-bg-border/50">
                    <td className="py-1.5 px-2 text-txt-primary">{fmtValue(r.symbol)}</td>
                    <td className={cn('py-1.5 px-2 font-medium', rowStatusCls(r.status))}>{ROW_STATUS_LABELS[r.status] || fmtValue(r.status)}</td>
                    <td className="py-1.5 px-2">{fmtValue(r.quantity)}</td>
                    <td className="py-1.5 px-2">{fmtValue(r.fill_price)}</td>
                    <td className="py-1.5 px-2 text-txt-secondary break-words">{r.message ? r.message : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-2 bg-bg-card border-t border-bg-border flex justify-end lg:static lg:mx-0 lg:px-0 lg:pt-0 lg:pb-0 lg:bg-transparent lg:border-0">
        <button type="button" onClick={onClose} className="btn-secondary min-h-[48px] lg:min-h-0">Close</button>
      </div>
    </>
  );
}
